/**
 * Invoice detail page orchestrator.
 *
 * @see prototype/sales/invoice-detail.html — document-first detail layout
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Alert, ConfirmDialog, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { WEB_ENV } from "../../../lib/env.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { usePermission } from "../../../lib/permissions.ts";
import {
  CreditNoteDialog,
  RequestVoidDialog,
  shimComputeInvoiceTotals,
  shimComputeLineTax,
  stateCodeFromGstin,
  stateCodeFromName,
  VoidInvoiceDialog,
} from "../index.ts";
import { RecordReceiptDialog } from "../../receipts/index.ts";
import { InvoiceDetailHeader } from "./invoice-detail-header.tsx";
import { InvoiceDetailRibbon } from "./invoice-detail-ribbon.tsx";
import { InvoiceDetailFoot } from "./invoice-detail-foot.tsx";
import { InvoiceDoc } from "./invoice-doc.tsx";
import { InvoiceRail } from "./invoice-rail.tsx";
import { InvoiceLinkedStrip } from "./invoice-linked-strip.tsx";
import { InvoiceTimeline } from "./invoice-timeline.tsx";

/** Route-owned invoice id for the detail page. */
export interface InvoiceDetailPageProps {
  invoiceId: string;
}

/**
 * Swap raw domain error messages (which embed the internal invoice UUID) for
 * plain-English copy on the detail-page load path (INVOICES-AC4, AC64). Mirrors
 * `describeLoadError` on the GRN detail page.
 */
function describeInvoiceLoadError(code: string | undefined): string {
  switch (code) {
    case "PARSE_ERROR":
    case "BAD_REQUEST":
      return "This invoice link isn't valid.";
    case "NOT_FOUND":
      return "Invoice not found.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have access to this invoice.";
    default:
      return "Couldn't load this invoice. Try again in a moment.";
  }
}

/**
 * Swap raw domain error messages (which embed internal invoice UUIDs, or a
 * raw Zod validation JSON blob) for plain-English, actionable copy. Mirrors
 * `describeVoidError` / `describeGrnMutationError` on the GRN side.
 */
function describeInvoiceMutationError(err: {
  data?: { code?: string | undefined } | undefined | null;
  message?: string | undefined | null;
} | null | undefined): string {
  if (!err) return "";
  const msg = err.message ?? "";
  // Status-guard 412s from findByIdForUpdate — "Invoice UUID is <status>; only drafts can be edited."
  if (/Invoice [0-9a-f-]{36} is (issued|paid|partially_paid|overdue|void|reversed); only drafts/i.test(msg)) {
    const status = msg.match(/is (\w+); only drafts/i)?.[1];
    return status
      ? `This invoice is ${status} — only drafts can be edited or deleted. Refresh to see the current state.`
      : "This invoice is no longer a draft. Refresh to see the current state.";
  }
  if (/At least one invoice line is required/i.test(msg)) {
    return "Add at least one line with a description before issuing.";
  }
  if (/customer.*not.*found|Customer [0-9a-f-]{36} not found/i.test(msg)) {
    return "The selected customer no longer exists. Pick another customer.";
  }
  // Raw Zod JSON — starts with `[` and contains `"code":"invalid_` or `"code":"too_small"`
  if (/^\[\s*\{[\s\S]*"code"\s*:\s*"(invalid_|too_small|too_big|custom)/.test(msg)) {
    return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
  }
  switch (err.data?.code) {
    case "PARSE_ERROR":
    case "BAD_REQUEST":
      return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
    case "NOT_FOUND":
      return "The requested invoice no longer exists. Refresh and try again.";
    case "PRECONDITION_FAILED":
      return "This invoice is no longer in a state that supports this action. Refresh and try again.";
    case "CONFLICT":
      return "This action conflicts with another change on the invoice. Refresh and try again.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have permission to complete this action.";
    default:
      return "Couldn't complete this action. Try again in a moment.";
  }
}

/** Load invoice detail data and compose the document-first invoice page. */
export function InvoiceDetailPage({ invoiceId }: InvoiceDetailPageProps) {
  const canIssue = usePermission("invoices.issue");
  const canSend = usePermission("invoices.send");
  const canVoid = usePermission("invoices.void");
  const canViewReceipts = usePermission("receipts.view");
  const canRecordReceipt = usePermission("receipts.record");
  const canViewCreditNotes = usePermission("creditNotes.view");
  const canRaiseCreditNote = usePermission("creditNotes.raise");
  const canExportReports = usePermission("reports.export");
  const navigate = useNavigate();
  const api = trpc as typeof trpc & Record<string, any>;
  const utils = api.useUtils() as Record<string, any>;
  const me = useQuery(meQueryOptions);
  const company = api.companies.get.useQuery({});
  const get = api.invoices.get.useQuery({ invoiceId });
  const receiptsForInvoice = api.receipts.list.useQuery(
    { limit: 50, invoiceId },
    { enabled: canViewReceipts },
  );
  const creditNotesForInvoice = api.creditNotes.list.useQuery(
    { limit: 50, invoiceId },
    { enabled: canViewCreditNotes },
  );
  const activityFeed = api.reports.invoiceActivityFeed.useInfiniteQuery(
    { invoiceId, limit: 10 },
    { getNextPageParam: (lastPage: { nextCursor?: string }) => lastPage.nextCursor },
  );
  const creditLineSummary = api.creditNotes.lineSummary.useQuery(
    { invoiceId },
    { enabled: canViewCreditNotes },
  );
  const activityEntries = useMemo(
    () => activityFeed.data?.pages.flatMap((p: { entries: unknown[] }) => p.entries) ?? [],
    [activityFeed.data],
  );

  const [voidOpen, setVoidOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const [requestVoidOpen, setRequestVoidOpen] = useState(false);

  const raiseCreditNote = api.creditNotes.raise.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.invoices.get.invalidate({ invoiceId }),
        utils.invoices.list.invalidate(),
        utils.creditNotes.list.invalidate(),
        utils.creditNotes.lineSummary.invalidate({ invoiceId }),
        utils.reports.invoiceActivityFeed.invalidate({ invoiceId }),
      ]);
      setCreditNoteOpen(false);
    },
  });
  const requestApproval = api.approvals.request.useMutation({
    onSuccess: async () => {
      await utils.approvals.list.invalidate();
      setRequestVoidOpen(false);
    },
  });
  const send = api.invoices.send.useMutation({
    onSuccess: async () => {
      await utils.invoices.get.invalidate({ invoiceId });
    },
  });
  const issue = api.invoices.issue.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.invoices.get.invalidate({ invoiceId }),
        utils.invoices.list.invalidate(),
      ]);
    },
  });
  const clone = api.invoices.clone.useMutation({
    onSuccess: async (data: { invoice: { id: string } }) => {
      await utils.invoices.list.invalidate();
      void navigate({ to: "/sales/invoices/$invoiceId/edit", params: { invoiceId: data.invoice.id } });
    },
  });
  const voidIt = api.invoices.void.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.invoices.get.invalidate({ invoiceId }),
        utils.invoices.list.invalidate(),
      ]);
      setVoidOpen(false);
    },
  });
  const deleteDraft = api.invoices.deleteDraft.useMutation({
    onSuccess: async () => {
      await utils.invoices.list.invalidate();
      void navigate({ to: "/sales/invoices" });
    },
  });

  /** Fetch the Tally voucher XML for this invoice and download it as a file. */
  async function handleExportTally(id: string) {
    try {
      const res = (await utils.tally.exportInvoice.fetch({ invoiceId: id })) as {
        fileName: string;
        xml: string;
      };
      const url = URL.createObjectURL(new Blob([res.xml], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tally XML downloaded", { description: res.fileName });
    } catch (err) {
      toast.error("Couldn't export to Tally", {
        description: (err as { message?: string }).message ?? "Unknown error",
      });
    }
  }

  if (get.isLoading || company.isLoading) {
    return <p className="text-sm text-muted">Loading invoice...</p>;
  }
  if (get.error || !get.data) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load invoice"
        description={describeInvoiceLoadError(get.error?.data?.code)}
      />
    );
  }
  if (company.error || !company.data) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load company profile"
        description={
          company.error?.message ?? "Settings → Company profile is required for invoice details."
        }
      />
    );
  }

  const inv = get.data.invoice;
  const lines = get.data.lines as Array<{
    id: string;
    position: number;
    description: string;
    hsn: string;
    qtyMilli: number;
    unit: string;
    ratePaise: string;
    discountBps: number;
    taxRateBps: number;
    lineTotalPaise: string;
    /** ADR-0020 — per-line foreign-minor values; null on INR invoices. */
    rateForeignMinor: string | null;
    lineTotalForeignMinor: string | null;
  }>;
  const customer = get.data.customer;
  const receipts = (receiptsForInvoice.data?.items ?? []) as Array<{
    id: string;
    appliedPaise: string;
    receivedOn: string;
    mode: string;
  }>;
  const creditNotes = (creditNotesForInvoice.data?.items ?? []) as Array<{
    id: string;
    docNumber: string;
    totalPaise: string;
  }>;
  const receivedPaise = receipts.reduce((sum, row) => sum + BigInt(row.appliedPaise), 0n);
  const creditedPaise = creditNotes.reduce((sum, row) => sum + BigInt(row.totalPaise), 0n);
  const isFullyCredited = creditedPaise >= BigInt(inv.totalPaise);
  const isFounder = me.data?.company?.role === "Founder";

  const companyStateCode =
    stateCodeFromGstin(company.data.gstin) ?? stateCodeFromName(company.data.state) ?? "";
  const computedLines = lines.map((line) =>
    shimComputeLineTax({
      qtyMilli: line.qtyMilli,
      ratePaise: BigInt(line.ratePaise),
      discountBps: line.discountBps,
      taxRateBps: line.taxRateBps,
    }),
  );
  const totals = shimComputeInvoiceTotals({
    lines: computedLines,
    companyStateCode,
    placeOfSupplyCode: inv.placeOfSupply,
  });

  const handleIssue = (sendVia: "whatsapp" | "email" | "print-later" | "issue-only") => {
    issue.mutate({
      draftId: inv.id,
      issuedOn: inv.issuedOn,
      dueOn: inv.dueOn,
      placeOfSupply: inv.placeOfSupply,
      sendVia,
    });
  };

  return (
    <div className="invoice-detail-page">
      <InvoiceDetailHeader
        invoice={inv}
        customer={customer}
        isFounder={isFounder}
        onEdit={() => navigate({ to: "/sales/invoices/$invoiceId/edit", params: { invoiceId: inv.id } })}
        onSendWhatsApp={() => send.mutate({ invoiceId: inv.id, channel: "whatsapp" })}
        onPrint={() => openInvoicePdf(inv.id, { download: false })}
        onDownloadPdf={() => openInvoicePdf(inv.id, { download: true, docNumber: inv.docNumber })}
        onIssue={handleIssue}
        onDeleteDraft={() => setDeleteOpen(true)}
        onRecordReceipt={() => setReceiptOpen(true)}
        onClone={() => clone.mutate({ invoiceId: inv.id })}
        onRaiseCreditNote={() => setCreditNoteOpen(true)}
        isFullyCredited={isFullyCredited}
        onVoid={() => setVoidOpen(true)}
        onRequestVoid={() => setRequestVoidOpen(true)}
        pendingSend={send.isPending}
        pendingIssue={issue.isPending}
        pendingClone={clone.isPending}
        canIssue={canIssue}
        canSend={canSend}
        canVoid={canVoid}
        canRecordReceipt={canRecordReceipt}
        canRaiseCreditNote={canRaiseCreditNote}
      />

      <div className="doc-stage">
        {/* Ribbon lives inside the doc column so its width matches the
         *  invoice paper below it — keeps the page visually anchored to
         *  the document rather than spilling across the rail. */}
        <div className="doc-stage-main">
          {/* Action-feedback banners live inside the doc column so their width
           *  matches the invoice paper below (same rule as the ribbon), rather
           *  than spilling full-width across the rail. */}
          {issue.isSuccess ? (
            <Alert intent="ok" title="Invoice issued" description="Document number assigned." />
          ) : null}
          {issue.error ? (
            <Alert intent="bad" title="Couldn't issue invoice" description={describeInvoiceMutationError(issue.error)} />
          ) : null}
          {send.isSuccess ? (
            <Alert intent="ok" title="Send queued" description="Queued via WhatsApp." />
          ) : null}
          {send.error ? (
            <Alert intent="bad" title="Couldn't send" description={describeInvoiceMutationError(send.error)} />
          ) : null}
          {clone.error ? (
            <Alert intent="bad" title="Couldn't clone" description={describeInvoiceMutationError(clone.error)} />
          ) : null}
          {deleteDraft.error ? (
            <Alert
              intent="bad"
              title="Couldn't delete draft"
              description={describeInvoiceMutationError(deleteDraft.error)}
            />
          ) : null}
          <InvoiceDetailRibbon
            status={inv.status}
            dueOn={inv.dueOn}
            totalPaise={inv.totalPaise}
            receivedPaise={receivedPaise}
            creditedPaise={creditedPaise}
          />
          <InvoiceDoc
            invoice={inv}
            company={company.data}
            customer={customer}
            lines={lines}
            totals={totals}
            creditedPaise={creditedPaise}
            lineCredits={
              (creditLineSummary.data?.lines ?? []) as Array<{
                sourceInvoiceLineId: string;
                qtyMilli: number;
                lineTotalPaise: string;
              }>
            }
            {...(isFullyCredited ? { reversal: { creditedPaise } } : {})}
          />
        </div>
        <InvoiceRail
          invoice={inv}
          customer={customer}
          totals={totals}
          receipts={receipts}
          creditedPaise={creditedPaise}
          isFullyCredited={isFullyCredited}
          onEdit={() =>
            navigate({ to: "/sales/invoices/$invoiceId/edit", params: { invoiceId: inv.id } })
          }
          onIssue={() => handleIssue("whatsapp")}
          onRecordReceipt={() => setReceiptOpen(true)}
          onSendReminder={() => send.mutate({ invoiceId: inv.id, channel: "whatsapp" })}
          onCreateCreditNote={() => setCreditNoteOpen(true)}
          onPrint={() => openInvoicePdf(inv.id, { download: false })}
          onClone={() => clone.mutate({ invoiceId: inv.id })}
          onExportTally={() => void handleExportTally(inv.id)}
          canIssue={canIssue}
          canRecordReceipt={canRecordReceipt}
          canSend={canSend}
          canCreateCreditNote={canRaiseCreditNote}
          canExportTally={canExportReports}
        />
      </div>

      <InvoiceLinkedStrip
        invoiceId={inv.id}
        poReference={inv.poReference}
        receipts={receipts}
        creditNotes={creditNotes}
        totalPaise={inv.totalPaise}
      />
      <InvoiceTimeline
        invoiceId={invoiceId}
        entries={activityEntries as never}
        isLoading={activityFeed.isLoading}
        fetch={{
          hasMore: Boolean(activityFeed.hasNextPage),
          isFetching: activityFeed.isFetchingNextPage,
          onLoadMore: () => {
            void activityFeed.fetchNextPage();
          },
        }}
      />

      {/* Sticky footer — summary only. Top header owns every action;
       *  this strip anchors the operator's context (customer · doc #
       *  · outstanding) when they've scrolled past the top. */}
      <InvoiceDetailFoot
        invoice={inv}
        customerName={customer.name}
        outstandingPaise={(BigInt(inv.totalPaise) - receivedPaise - creditedPaise).toString()}
      />

      <VoidInvoiceDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        docNumber={inv.docNumber || null}
        pending={voidIt.isPending}
        {...(voidIt.error ? { errorMessage: describeInvoiceMutationError(voidIt.error) } : {})}
        onConfirm={(reason) => voidIt.mutate({ invoiceId: inv.id, reason })}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this draft?"
        description="Drafts can be safely deleted because no document number has been allocated."
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting..." : "Delete draft"}
        onConfirm={() => deleteDraft.mutate({ invoiceId: inv.id })}
      />
      <RecordReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        invoice={{
          id: inv.id,
          docNumber: inv.docNumber || null,
          customerId: inv.customerId,
          totalPaise: inv.totalPaise,
          receivedSoFarPaise: receivedPaise.toString(),
          currencyCode: inv.currencyCode,
          foreignAmountMinor: inv.foreignAmountMinor,
          fxRateToInrPaise: inv.fxRateToInrPaise,
        }}
        customer={{
          id: customer.id,
          name: customer.name,
          isTdsApplicable: customer.isTdsApplicable ?? false,
          tdsSection: customer.tdsSection ?? null,
        }}
        onRecorded={async () => {
          await Promise.all([
            utils.invoices.get.invalidate({ invoiceId }),
            utils.invoices.list.invalidate(),
            utils.receipts.list.invalidate(),
          ]);
        }}
      />
      <CreditNoteDialog
        open={creditNoteOpen}
        onOpenChange={setCreditNoteOpen}
        invoiceDocNumber={inv.docNumber || "this invoice"}
        invoiceTotalLabel={new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
        }).format(Number(BigInt(inv.totalPaise)) / 100)}
        lines={lines.map((l) => ({
          id: l.id,
          position: l.position,
          description: l.description,
          qtyMilli: l.qtyMilli,
          unit: l.unit,
          ratePaise: l.ratePaise,
          discountBps: l.discountBps,
          taxRateBps: l.taxRateBps,
          lineTotalPaise: l.lineTotalPaise,
        }))}
        pending={raiseCreditNote.isPending}
        {...(raiseCreditNote.error ? { errorMessage: describeInvoiceMutationError(raiseCreditNote.error) } : {})}
        onConfirm={({ reason, lines: pickedLines }) =>
          raiseCreditNote.mutate({
            againstInvoiceId: inv.id,
            reason,
            ...(pickedLines ? { lines: pickedLines } : {}),
          })
        }
      />
      <RequestVoidDialog
        open={requestVoidOpen}
        onOpenChange={setRequestVoidOpen}
        docNumber={inv.docNumber || null}
        invoiceId={inv.id}
        pending={requestApproval.isPending}
        {...(requestApproval.error ? { errorMessage: describeInvoiceMutationError(requestApproval.error) } : {})}
        onConfirm={({ reason }) =>
          requestApproval.mutate({
            kind: "void_invoice",
            summary: `Operator requested void of ${inv.docNumber || "this invoice"} - ${reason.slice(0, 100)}`,
            entityType: "invoice",
            entityId: inv.id,
            requestPayload: { reason },
          })
        }
      />
    </div>
  );
}

/**
 * Open the server-side PDF endpoint for an invoice.
 *
 * - Print: opens in a new tab; Chrome's inline PDF viewer offers its own
 *   print button so we don't need to trigger the OS print dialog ourselves.
 * - Download: builds a hidden anchor with `download` set and clicks it so
 *   the browser saves the file with the invoice's doc number as the
 *   filename.
 *
 * The endpoint authenticates via the existing session cookie (same as tRPC),
 * so a same-origin GET is enough — no Authorization header to manage.
 *
 * @see apps/api/src/routes/invoice-pdf.ts — the server route.
 */
function openInvoicePdf(
  invoiceId: string,
  opts: { download: boolean; docNumber?: string | null },
): void {
  // Use the absolute API origin so the request bypasses the Vite dev
  // server's SPA fallback and hits Hono on port 3000. Same pattern used by
  // the tRPC client and the AI chat fetcher.
  const base = `${WEB_ENV.VITE_FLINT_API_URL}/api/invoices/${invoiceId}/pdf`;
  if (!opts.download) {
    window.open(base, "_blank", "noopener");
    return;
  }
  const a = document.createElement("a");
  a.href = `${base}?download=1`;
  a.download = `${opts.docNumber && opts.docNumber.length > 0 ? opts.docNumber : "invoice"}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
